import type { PatientV2 } from "../types";

function formatDateYmd(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatYmdHmInTimeZone(date: Date, timeZone: string) {
  const dtf = new Intl.DateTimeFormat("sv-SE", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value;
  const y = get("year");
  const m = get("month");
  const d = get("day");
  const h = get("hour");
  const min = get("minute");
  if (!y || !m || !d || !h || !min) return { ymd: "", hm: "" };
  return { ymd: `${y}-${m}-${d}`, hm: `${h}:${min}` };
}

function parseYmdHm(localYmdHm: string) {
  const [d, t] = localYmdHm.split("T");
  if (!d || !t) return null;
  const [y, m, day] = d.split("-").map((v) => Number(v));
  const [hh, mm] = t.slice(0, 5).split(":").map((v) => Number(v));
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hh) ||
    !Number.isFinite(mm)
  ) {
    return null;
  }
  return { y, m, day, hh, mm, ymd: d, hm: t.slice(0, 5) };
}

function nursingNoteLocalToUtcIso(localYmdHm: string, timeZone: string) {
  const parsed = parseYmdHm(localYmdHm);
  if (!parsed) return null;
  const desiredUtcLike = Date.UTC(parsed.y, parsed.m - 1, parsed.day, parsed.hh, parsed.mm, 0, 0);
  let guess = new Date(desiredUtcLike);
  for (let i = 0; i < 4; i++) {
    const got = formatYmdHmInTimeZone(guess, timeZone);
    if (!got.ymd || !got.hm) break;
    const gotParsed = parseYmdHm(`${got.ymd}T${got.hm}`);
    if (!gotParsed) break;
    const gotUtcLike = Date.UTC(gotParsed.y, gotParsed.m - 1, gotParsed.day, gotParsed.hh, gotParsed.mm, 0, 0);
    const diffMs = desiredUtcLike - gotUtcLike;
    if (diffMs === 0) break;
    guess = new Date(guess.getTime() + diffMs);
  }
  return guess.toISOString();
}

function patientNowYmdHm(timeZone?: string) {
  if (typeof timeZone === "string" && timeZone.trim()) {
    try {
      const nowInPatientTz = formatYmdHmInTimeZone(new Date(), timeZone);
      if (nowInPatientTz.ymd && nowInPatientTz.hm) return nowInPatientTz;
    } catch {
      // fall through
    }
  }
  const now = new Date();
  return {
    ymd: formatDateYmd(now),
    hm: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
  };
}

function nursingNoteMaxSelectableYmd(timeZone?: string) {
  return patientNowYmdHm(timeZone).ymd;
}

export function getPatientTimeZone(patient: PatientV2 | null | undefined): string | undefined {
  const facility = patient?.facilityId;
  const organization = patient?.organizationId;
  const tz =
    (typeof facility === "object" ? facility?.timezone || facility?.timeZone : undefined) ||
    (patient as Record<string, unknown> | undefined)?.resolvedTimezone ||
    (typeof organization === "object" ? organization?.timezone || organization?.timeZone : undefined) ||
    patient?.timezone;
  return typeof tz === "string" && tz.trim() ? tz.trim() : undefined;
}

export function splitNursingNoteDateTime(dt: string) {
  if (!dt || typeof dt !== "string") return { dateYmd: "", timeHm: "00:00" };
  const [d, tPart = "00:00"] = dt.split("T");
  const timeHm = tPart.length >= 5 ? tPart.slice(0, 5) : "00:00";
  return { dateYmd: d || "", timeHm };
}

export function joinNursingNoteDateTime(dateYmd: string, timeHm: string) {
  if (!dateYmd) return "";
  const t = timeHm && /^\d{2}:\d{2}$/.test(timeHm) ? timeHm : "00:00";
  return `${dateYmd}T${t}`;
}

export function toNursingNoteFormDateTime(note: Record<string, unknown> | null | undefined): string {
  const local = note?.dateTimeLocal;
  if (typeof local === "string" && local.includes("T")) return local.slice(0, 16);

  const raw = note?.dateTime;
  if (typeof raw === "string" && raw) {
    try {
      return new Date(raw).toLocaleString("sv-SE").slice(0, 16);
    } catch {
      // fall through
    }
  }
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function isNursingNoteDateBeyondPatientNow(ymd: string, timeZone?: string) {
  if (!ymd) return false;
  return ymd > nursingNoteMaxSelectableYmd(timeZone);
}

export function isNursingNoteDateTimeInFutureForPatient(combined: string, timeZone?: string) {
  const parsed = parseYmdHm(combined);
  if (!parsed) return false;
  const selectedUtcIso = timeZone
    ? nursingNoteLocalToUtcIso(combined, timeZone)
    : new Date(combined).toISOString();
  if (!selectedUtcIso) return false;
  const selectedMs = new Date(selectedUtcIso).getTime();
  if (!Number.isFinite(selectedMs)) return false;
  return selectedMs > Date.now();
}

export function nursingNoteFormDateTimeToUtcIso(combined: string, timeZone?: string) {
  if (timeZone) {
    const fromPatientTz = nursingNoteLocalToUtcIso(combined, timeZone);
    if (fromPatientTz) return fromPatientTz;
  }
  const fallback = new Date(combined);
  return Number.isNaN(fallback.getTime()) ? null : fallback.toISOString();
}

export function buildNursingNoteFormFromNote(
  note: Record<string, unknown>,
  programOptions: { value: string }[],
) {
  const programValue = note?.program ? String(note.program).toLowerCase().trim() : "";
  const validProgram =
    programOptions.find((opt) => opt.value === programValue)?.value || programValue || "";

  return {
    program: validProgram,
    careType: note?.careType
      ? Array.isArray(note.careType)
        ? (note.careType as string[])
        : String(note.careType).split(",")
      : [],
    dateTime: toNursingNoteFormDateTime(note),
    duration: note?.duration != null ? String(note.duration) : "",
    notes: String(note?.content ?? ""),
    note_type: String(note?.note_type ?? ""),
  };
}
